import {IInterimLimits, nextFirstAndFifteenthDates} from "../../../../account/payment-plans/utils";
import dayjs, {Dayjs} from "dayjs";
import {DATE_FORMAT, multiply, subtract} from "@bounce/shared/helpers";
import {AccountExternal} from "../../../../../declerations/server/account_models";
import {PaymentPlanFrequency} from "@bounce/shared/enums";
import {
  PaymentPlanOffer,
  PaymentPlanParts,
  PaymentPlanTerms,
} from "../../../../../declerations/server/payment_plan_models";
import {PaymentDetails} from "../../../../../declerations/server";
import {ICustomizedPaymentPlanDetails} from "./components/custom-view/useCustomView";

export type modeType = "build-plan" | "dashboard";

enum PLAN_STEPS {
  CHOOSE_PLAN = "choose_plan",
  SUMMARY = "summary",
  PAYMENT = "payment",
  AUTHORIZATION = "authorization",
}

export interface ICustomPaymentPlanViewProps {
  userBalance: AccountExternal;
  isSupervisorView: boolean;
  setIsSupervisorView: (i: boolean) => void;
}

const getMaxNumOfPayments = (freq: PaymentPlanFrequency, maxMonthlyPlanLength: number): number => {
  switch (freq) {
    case "monthly":
      return maxMonthlyPlanLength;
    case "one_time":
      return 1;
    case "weekly":
      return maxMonthlyPlanLength * 4;
    case "bi_weekly":
    case "first_and_fifteenth":
      return maxMonthlyPlanLength * 2;
    default:
      return maxMonthlyPlanLength;
  }
};

const getMinInstallmentsValues = (
  totalBalance: number,
  minInstallmentSize: number,
  maxMonthlyPlanLength: number,
  freq: PaymentPlanFrequency
): {minInstallmentSize: number; numberOfPayments: number} => {
  const maxInstallmentsNumber = getMaxNumOfPayments(freq, maxMonthlyPlanLength);
  let currentMinInstallmentSize;
  if (totalBalance / maxInstallmentsNumber < minInstallmentSize) {
    currentMinInstallmentSize = minInstallmentSize;
  } else {
    currentMinInstallmentSize = Math.ceil(totalBalance / maxInstallmentsNumber);
  }
  const numberOfInstallments = Math.ceil(totalBalance / currentMinInstallmentSize);
  return {minInstallmentSize: currentMinInstallmentSize, numberOfPayments: numberOfInstallments};
};

const getPercentage = ({originalBalance, discountedBalance}: {originalBalance: number; discountedBalance: number}) => {
  if (originalBalance > 0 && discountedBalance > 0) {
    const discountAmount = originalBalance - discountedBalance;
    return Number((discountAmount / originalBalance) * 100);
  } else {
    return 0;
  }
};

const enabledDates = (date: Dayjs) => nextFirstAndFifteenthDates({numberOfDays: 365 * 3, startDate: date});

const shouldDisableDate = (date: Dayjs) => {
  return !enabledDates(date).some(
    (enabledDate) => dayjs(date).format(DATE_FORMAT) === dayjs(enabledDate).format(DATE_FORMAT)
  );
};

const getBackButtonLabel: Record<modeType, string> = {
  dashboard: "Switch to a full plan",
  "build-plan": "Back to dashboard",
};

const planSteps = [PLAN_STEPS.CHOOSE_PLAN, PLAN_STEPS.SUMMARY, PLAN_STEPS.PAYMENT];
const interimPlanSteps = [PLAN_STEPS.CHOOSE_PLAN, PLAN_STEPS.SUMMARY, PLAN_STEPS.PAYMENT, PLAN_STEPS.AUTHORIZATION];
const customPlanSteps = [PLAN_STEPS.CHOOSE_PLAN, PLAN_STEPS.PAYMENT, PLAN_STEPS.AUTHORIZATION];

export type InstallmentsPlanTabsType = "by-amount" | "by-num-of-installments";
const installmentsPaymentPlanTabs = {
  byAmount: {
    id: "by-amount",
    name: "Amount",
  },
  byNumOfInstallments: {
    id: "by-num-of-installments",
    name: "Installments",
  },
};

const mapMaxInstallmentByFrequency = (interimLimits: IInterimLimits) => {
  return {
    weekly: interimLimits.max_installment_size_weekly,
    bi_weekly: interimLimits.max_installment_size_biweekly,
    first_and_fifteenth: interimLimits.max_installment_size_biweekly,
    monthly: interimLimits.max_installment_size_monthly,
    one_time: interimLimits.max_installment_size_monthly,
    last_business_day_of_the_month: interimLimits.max_installment_size_monthly,
    non_linear: 0,
    tax_season: interimLimits.max_installment_size_monthly,
  };
};

const durationDetails: Record<string, {multiply: number; label: string}> = {
  weekly: {multiply: 4, label: "Every Week"},
  bi_weekly: {multiply: 2, label: "Every 2 Weeks"},
  first_and_fifteenth: {multiply: 2, label: "Every 1st & 15th"},
  monthly: {multiply: 1, label: "Every Month"},
  one_time: {multiply: 2, label: "One time"},
};

const MonthsDurations = [3, 6, 9];

const getLastPaymentAmount = (paymentPlanOffer: PaymentPlanOffer) => {
  if (paymentPlanOffer.total_amount === paymentPlanOffer.number_of_installments * paymentPlanOffer.installment_size) {
    return {lastPayment: 0, numOfInstallments: paymentPlanOffer.number_of_installments};
  } else {
    return {
      lastPayment: subtract(
        paymentPlanOffer.total_amount,
        multiply(paymentPlanOffer.installment_size, subtract(paymentPlanOffer.number_of_installments, 1))
      ),
      numOfInstallments: subtract(paymentPlanOffer.number_of_installments, 1),
    };
  }
};

export type UserPlanTerms = {
  isAgent: boolean;
  userId: string;
  paymentPlanTerms: PaymentPlanTerms;
};

const getPaymentPlanTerms = (
  userId: string,
  accountId: string,
  paymentDetails: PaymentDetails,
  type: string,
  isAgent: boolean,
  finalInstallments?: ICustomizedPaymentPlanDetails,
  paymentPlanOffer?: PaymentPlanOffer,
  remainder?: number
): UserPlanTerms | undefined => {
  if (!!finalInstallments && !!paymentPlanOffer) {
    return;
  }
  const paymentMethod = paymentDetails?.credit_card ? "debit card" : "ach";
  const last4Digits = paymentDetails?.credit_card
    ? paymentDetails.credit_card.card_number.slice(-4)
    : paymentDetails?.ach?.account_number.slice(-4) || "";
  const remainderString = remainder ? remainder.toString() : undefined;

  if (type === "non_linear" && !!finalInstallments) {
    return {
      isAgent,
      userId,
      paymentPlanTerms: {
        account_id: accountId,
        last_4_digits: last4Digits,
        payment_method: paymentMethod,
        payment_plan_parts: finalInstallments.customizedPaymentPlan.payment_plans.map((paymentPlanPart) => {
          return {
            num_of_payments: paymentPlanPart.number_of_installments.toString(),
            installment_amount: paymentPlanPart.installment_size.toString(),
            frequency: paymentPlanPart.installment_frequency,
            start_date: paymentPlanPart.start_date,
            end_date: paymentPlanPart.end_date,
          };
        }),
        single_installment_plan_parts: finalInstallments.customizedPaymentPlan.installments.map(
          (singleInstallmentPlanPart) => {
            return {
              installment_amount: singleInstallmentPlanPart.amount_cents.toString(),
              start_date: singleInstallmentPlanPart.due_date,
            };
          }
        ),
        remainder: remainderString,
        type: type,
        total_amount: finalInstallments.totalAmount.toString(),
      },
    };
  }

  if (paymentPlanOffer) {
    return {
      isAgent,
      userId,
      paymentPlanTerms: {
        account_id: accountId,
        last_4_digits: last4Digits,
        payment_method: paymentMethod,
        payment_plan_parts: [
          {
            num_of_payments: paymentPlanOffer?.number_of_installments.toString(),
            installment_amount: String(paymentPlanOffer?.installment_size),
            frequency: paymentPlanOffer?.installment_frequency,
            start_date: paymentPlanOffer?.start_date,
            end_date: paymentPlanOffer?.end_date,
          } as PaymentPlanParts,
        ],
        single_installment_plan_parts: [],
        remainder: remainderString,
        type: type,
        total_amount: paymentPlanOffer?.total_amount.toString(),
      },
    };
  }
};

export {
  PLAN_STEPS,
  getMinInstallmentsValues,
  getPercentage,
  enabledDates,
  shouldDisableDate,
  getBackButtonLabel,
  planSteps,
  interimPlanSteps,
  customPlanSteps,
  installmentsPaymentPlanTabs,
  mapMaxInstallmentByFrequency,
  durationDetails,
  MonthsDurations,
  getLastPaymentAmount,
  getPaymentPlanTerms,
};
